import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

const SearchableDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  placeholderClassName = "",
  label,
  required = false,
  searchPlaceholder = "Search...",
  noResultsText = "No results found",
  disabled = false,
  labelKey = "name"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropDirection, setDropDirection] = useState('down');
  const dropdownRef = useRef(null);

  const selectedOption = (options || []).find(option => option._id === value?._id || option._id === value);
  
  const filteredOptions = (options || []).filter(option =>
    (option[labelKey] || option.name || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
    (option.subtitle && option.subtitle.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < 250 && spaceAbove > spaceBelow) {
        setDropDirection('up');
      } else {
        setDropDirection('down');
      }

      // Small delay to ensure render then scroll
      setTimeout(() => {
        dropdownRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (option) => {
    if (disabled) return;
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange('');
  };

  return (
    <div className={`relative ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`} ref={dropdownRef}>
      {label && (
        <label className={`text-[11px] font-black uppercase tracking-widest mb-1 ml-1 ${disabled ? 'text-gray-400' : 'text-slate-400'}`}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div
        className={`w-full px-3 py-2 border rounded-xl bg-white flex items-center justify-between ${
          disabled 
            ? 'border-gray-200 bg-gray-50 pointer-events-none' 
            : 'border-slate-300 focus-within:ring-4 focus-within:ring-blue-500/10 focus-within:border-blue-500 cursor-pointer shadow-sm'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex-1">
          {selectedOption ? (
            <div>
              <p className={`font-medium ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>
                {selectedOption[labelKey] || selectedOption.name}
                {selectedOption.section && labelKey !== 'section' && <span className="text-slate-400 font-bold ml-1.5">- Section {selectedOption.section}</span>}
              </p>
              {selectedOption.subtitle && (
                <p className="text-sm text-gray-600">{selectedOption.subtitle}</p>
              )}
            </div>
          ) : (
            <span className={`text-gray-500 ${placeholderClassName}`}>{placeholder}</span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {selectedOption && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={16} className="text-gray-400" />
            </button>
          )}
          <ChevronDown 
            size={20} 
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </div>

      {isOpen && !disabled && (
        <div className={`absolute z-[100] w-full bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-blue-900/10 max-h-64 overflow-hidden animate-fadeIn backdrop-blur-xl ${
          dropDirection === 'up' ? 'bottom-full mb-2' : 'mt-2'
        }`}>
          <div className="p-3 border-b border-slate-100 bg-blue-50/30">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl py-2 pl-9 pr-3 text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                autoFocus
              />
            </div>
          </div>
          
          <div className="max-h-48 overflow-y-auto pb-2">
            {/* {!required && (
              <div
                className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                onClick={() => handleSelect('')}
              >
                <p className="text-gray-500">No incharge selected</p>
              </div>
            )} */}
            
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option._id}
                  className={`w-full text-left px-4 py-3 text-sm font-bold transition-all duration-200 border-b border-slate-50 last:border-0 cursor-pointer ${
                    (value?._id === option._id || value === option._id) 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  <p>
                    {option[labelKey] || option.name}
                    {option.section && labelKey !== 'section' && <span className="opacity-60 font-bold ml-1.5">- Section {option.section}</span>}
                  </p>
                  {option.subtitle && (
                    <p className="text-sm text-gray-600 font-medium">{option.subtitle}</p>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 text-center">
                {noResultsText}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;